import React, { useState, useEffect } from 'react';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
  CheckIcon,
  XIcon,
  InfoIcon,
} from 'venuescanner-js/react/components/Objects/Icons/FeatherIcons';
import { Box, Modal, Button } from '@mui/material';

import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { take, groupBy } from 'lodash';
import { markEnquiryAsBooked } from '../../enquiryMachine/api';
import inboxService from 'venuescanner-js/react/components/Inbox/services/inboxService';
import { authService } from 'venuescanner-js/services/auth';
import MembershipPromoModal from 'venuescanner-js/react/components/Inbox/components/Promotions/MembershipPromoModal';
import MoreBookingsPromoModal from 'venuescanner-js/react/components/Inbox/components/Promotions/MoreBookingsPromoModal';
import subPlans from 'venuescanner-js/constants/subscriptions';
import PropTypes from 'prop-types';

import {
  WeeklySummaryBox,
  ThreadsList,
  VenueTitle,
  ThreadBox,
  ThreadInfoBox,
  BookedButton,
  DeclinedButton,
  OngoingButton,
  OngoingSelectedAlert,
  ButtonBox,
  ReminderTooltip,
  RemindMeLaterButton,
  SubmitButton,
  SubmitButtonBox,
  SubmitButtonSpinner,
} from './components';
import {
  useGetOngoingThreads,
  useGetFlags,
  useSetFlags,
  useHasSubscriptionToAllVenues,
} from './queries';

dayjs.extend(isBetween);

const THREAD_STATUS = {
  BOOKED: 'booked',
  DECLINED: 'declined',
  ONGOING: 'ongoing',
};

const YourRecentEnquiriesModal = ({
  onBooked = markEnquiryAsBooked,
  onClosed = inboxService['declineThread'].bind(inboxService),
}) => {
  const [open, setOpenState] = useState(true);
  const [openPromotion, setOpenPromotionState] = useState(false);
  const [ongoingThreads, setOngoingThreads] = useState([]);
  const [groupedOngoingThreads, setGroupedOngoingThreads] = useState({});
  const [threadsStatus, setThreadsStatus] = useState({});
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [enableGetOngoingThreads, setEnableGetOngoingThreads] = useState(false);
  const [bookedThreads, setBookedThreads] = useState([]);

  const flagsQuery = useGetFlags();
  const ongoingThreadsQuery = useGetOngoingThreads({
    enabled: enableGetOngoingThreads,
  });
  const setFlagsMutation = useSetFlags();
  const hasSubscriptionToAllVenuesQuery = useHasSubscriptionToAllVenues({
    enabled: enableGetOngoingThreads,
  });

  const parseThreads = threads => {
    setOngoingThreads(threads);
    const threadsByVenue = groupBy(
      [
        ...threads.sort((a, b) => {
          const firstDate = dayjs(a?.date_from);
          const secondDate = dayjs(b?.date_from);
          if (firstDate.isValid() && secondDate.isValid()) {
            return firstDate.isBefore(secondDate) ? 1 : -1;
          }
        }),
      ],

      'venue.name',
    );
    setGroupedOngoingThreads(threadsByVenue);
    const threadsReduced = threads.reduce((threadsObj, thread) => {
      threadsObj[thread._id] = '';
      return threadsObj;
    }, {});
    setThreadsStatus(threadsReduced);
  };

  useEffect(() => {
    if (flagsQuery.isSuccess && shouldGetThreads(flagsQuery.data)) {
      setEnableGetOngoingThreads(true);
    }
  }, [flagsQuery.isSuccess, flagsQuery.data]);

  useEffect(() => {
    if (ongoingThreadsQuery.data?.length) {
      parseThreads(take(ongoingThreadsQuery.data, 5));
    }
  }, [ongoingThreadsQuery.data]);

  useEffect(() => {
    setSubmitDisabled(
      Object.values(threadsStatus).some(
        threadStatus => threadStatus?.length === 0,
      ),
    );
  }, [threadsStatus]);

  const handleThreadStatus = (threadId, event) => {
    const currentState = threadsStatus;
    currentState[threadId] = event.target.value;
    setThreadsStatus(previousState => ({
      ...previousState,
      ...currentState,
    }));
  };

  const handleSubmit = () => {
    const bookedDeclinedThreads = { booked: [], declined: [] };

    Object.entries(threadsStatus).forEach(([threadId, status]) => {
      if (status === THREAD_STATUS.BOOKED) {
        bookedDeclinedThreads.booked.push(threadId);
      }

      if (status === THREAD_STATUS.DECLINED) {
        bookedDeclinedThreads.declined.push(threadId);
      }
    });

    bookedDeclinedThreads.booked.forEach(
      threadId => onBooked(threadId),
      // markEnquiryAsBooked(threadId)
    );
    bookedDeclinedThreads.declined.forEach(threadId => {
      const thread = ongoingThreads.find(
        singleThread => singleThread._id === threadId,
      );

      onClosed({
        _id: thread._id,
        offer: thread.cumulativeOffer,
        message: '',
        attachments: [],
        reason: false,
      });
    });

    setFlagsMutation.mutate({
      submittedDate: dayjs().toISOString(),
    });

    const populatedBookedThreads = bookedDeclinedThreads.booked.map(threadId =>
      ongoingThreads.find(singleThread => singleThread._id === threadId),
    );
    setSubmitDisabled(true);
    setThreadsStatus({});
    setGroupedOngoingThreads({});
    setOngoingThreads([]);
    setOpenState(false);
    setBookedThreads(populatedBookedThreads);
    setOpenPromotionState(true);
  };

  const handleRemindMeLater = () => {
    setFlagsMutation.mutate({
      reminderDate: dayjs().add(2, 'hour').toISOString(),
    });
    setOpenState(false);
  };

  const isOngoingSelected = () => {
    return !!Object.values(threadsStatus).find(
      threadStatus => threadStatus === THREAD_STATUS.ONGOING,
    );
  };

  const canShowRemindMeLater = () =>
    flagsQuery.isSuccess &&
    (!flagsQuery.data?.reminderDate ||
      !isDateWithinThisWeek(flagsQuery.data.reminderDate));

  return (
    <>
      {!hasSubscriptionToAllVenuesQuery?.data && (
        <PromotionalModal
          bookedThreads={bookedThreads}
          open={openPromotion}
          onClose={() => setOpenPromotionState(false)}
        />
      )}
      {!!Object.keys(ongoingThreads).length && (
        <Modal
          open={open}
          onClose={() => {}}
          aria-labelledby='recent-enquiries-modal-title'
          aria-describedby='recent-enquiries-modal-description'
          disableEscapeKeyDown={true}>
          <WeeklySummaryBox>
            {!!authService.admin && (
              <Button
                sx={{ mb: 1 }}
                variant='outlined'
                onClick={() => setOpenState(false)}>
                Close modal (I&apos;m an admin)
              </Button>
            )}
            <Box component='h3' id='recent-enquiries-modal-title'>
              Your recent events
            </Box>
            <Box
              component='p'
              className='weekly-summary__subtitle'
              id='recent-enquiries-modal-description'>
              These enquiries were due to happen recently and are still open in
              your inbox. Remember, you&apos;ll get a boost in search for every
              confirmed booking.
            </Box>
            <ThreadsList>
              {Object.entries(groupedOngoingThreads).map(
                ([venueName, venueOngoingThreads]) => {
                  return (
                    <Box key={venueOngoingThreads[0].venue._id}>
                      <VenueTitle component='h2'>{venueName}</VenueTitle>
                      {venueOngoingThreads.map(ongoingThread => {
                        return (
                          <Box
                            key={ongoingThread._id}
                            className='weekly-summary__thread'>
                            <ThreadBox>
                              <ThreadInfoBox>
                                <Box
                                  component='p'
                                  className='weekly-summary__thread--info'>
                                  {ongoingThread.event_type}{' '}
                                  <Box component='span'>
                                    on{' '}
                                    {dayjs(ongoingThread.date_from).format(
                                      'DD.MM.YYYY',
                                    )}
                                  </Box>
                                </Box>
                                <Box
                                  component='p'
                                  className='weekly-summary__thread--user'>
                                  {ongoingThread.organiser?.name} -{' '}
                                  {ongoingThread.organiser?.email}
                                </Box>
                              </ThreadInfoBox>
                              <Box>
                                <ToggleButtonGroup
                                  exclusive
                                  value={threadsStatus[ongoingThread._id]}
                                  onChange={e =>
                                    handleThreadStatus(ongoingThread._id, e)
                                  }
                                  disabled={setFlagsMutation.isLoading}>
                                  <BookedButton
                                    value={THREAD_STATUS.BOOKED}
                                    aria-label='Booked'>
                                    <CheckIcon color='#424242' /> Booked
                                  </BookedButton>
                                  <DeclinedButton
                                    value={THREAD_STATUS.DECLINED}
                                    aria-label='Closed'>
                                    <XIcon color='#424242' /> Closed
                                  </DeclinedButton>
                                  <OngoingButton
                                    value={THREAD_STATUS.ONGOING}
                                    aria-label='Ongoing'>
                                    Ongoing
                                  </OngoingButton>
                                </ToggleButtonGroup>
                              </Box>
                            </ThreadBox>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                },
              )}

              {isOngoingSelected() && (
                <OngoingSelectedAlert severity='warning'>
                  If the event dates have changed then you can update the
                  details in your Inbox
                </OngoingSelectedAlert>
              )}
              <ButtonBox>
                {canShowRemindMeLater() && (
                  <ReminderTooltip
                    title={
                      <>
                        {
                          'In a rush? Click Remind me later to skip this screen this time. '
                        }
                        <br />
                        {
                          'The next time you’ll need to update the statuses before you can proceed.'
                        }
                      </>
                    }>
                    <RemindMeLaterButton
                      variant='text'
                      endIcon={<InfoIcon />}
                      onClick={handleRemindMeLater}
                      disabled={false}>
                      Remind me later
                    </RemindMeLaterButton>
                  </ReminderTooltip>
                )}
                <SubmitButtonBox>
                  <SubmitButton
                    disabled={!!submitDisabled || setFlagsMutation.isLoading}
                    onClick={handleSubmit}
                    variant='contained'
                    color='primary'
                    size='large'>
                    Submit
                  </SubmitButton>
                  {setFlagsMutation.isLoading && (
                    <SubmitButtonSpinner size={24} />
                  )}
                </SubmitButtonBox>
              </ButtonBox>
            </ThreadsList>
          </WeeklySummaryBox>
        </Modal>
      )}
    </>
  );
};

YourRecentEnquiriesModal.propTypes = {
  onBooked: PropTypes.func,
  onClosed: PropTypes.func,
};

export default YourRecentEnquiriesModal;

export function isDateWithinThisWeek(dateStr) {
  const startOfThisWeeksMonday = dayjs().day(1).startOf('day');
  const endOfThisWeek = dayjs().endOf('week');

  // Start of week <= now < end of week
  return dayjs(dateStr).isBetween(
    startOfThisWeeksMonday,
    endOfThisWeek,
    'hour',
    '[)',
  );
}

function isNowAfter(dateStr) {
  return dayjs().isAfter(dayjs(dateStr));
}

const hasSubmitted = flags =>
  flags.submittedDate && isDateWithinThisWeek(flags.submittedDate);

const isTimeToRemind = flags =>
  flags.reminderDate && isNowAfter(flags.reminderDate);

export function shouldGetThreads(flags) {
  if (!flags) {
    return true;
  }

  if (!hasSubmitted(flags) && isTimeToRemind(flags)) {
    return true;
  }

  if (!hasSubmitted(flags) && (!flags.reminderDate || isTimeToRemind(flags))) {
    return true;
  }

  return false;
}

const PromotionalModal = ({ open, bookedThreads, onClose }) => {
  if (!bookedThreads.length) {
    return <MoreBookingsPromoModal open={open} onClose={onClose} />;
  }

  const areAllVenuesSubscribed = bookedThreads.every(isThreadVenueSubscribed);

  return areAllVenuesSubscribed ? null : (
    <MembershipPromoModal open={open} onClose={onClose} />
  );
};

function isThreadVenueSubscribed({ venue }) {
  return hasSubscription(venue);
}

function hasSubscription({ sub_plan: subPlan }) {
  return subPlan
    ? Object.values(subPlans).includes(subPlan.toLowerCase())
    : false;
}

PromotionalModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  bookedThreads: PropTypes.array.isRequired,
};
